import CfSectionShell from '../cf-section-shell'
import type { CfSectionShellFields } from '../cf-section-shell'
import type { CfImage } from '~/types/contentful'
import { LinkCtaNext } from '~/components-v2'
import clsx from 'clsx'
import { contentfulImage } from '~/utils/contentful-image'

export interface GuidesFields {
  copy?: string
  ctaLabel?: string
  ctaUrl?: string
  headline?: string
  image?: CfImage
}

interface CfGuidesSectionProps {
  fields: CfSectionShellFields & GuidesFields
}

const copyToParagraphs = (copy?: string) =>
  typeof copy === 'string'
    ? copy
        .split(/\n{2,}/)
        .map((paragraph) => paragraph.trim())
        .filter(Boolean)
    : []

const isExternalUrl = (url: string) => /^https?:\/\//i.test(url)

export function CfGuidesSection({ fields }: CfGuidesSectionProps) {
  const {
    id,
    paddingTop,
    paddingBottom,
    showBottomBorder,
    theme,
    fullWidth,
    mobileNoTopPadding,
    copy,
    ctaLabel,
    ctaUrl,
    headline,
    image,
  } = fields

  const paragraphs = copyToParagraphs(copy)
  const imageUrl = image?.fields?.file?.url
  const imageAlt = image?.fields?.title ?? headline ?? ''
  const ctaIsExternal = ctaUrl ? isExternalUrl(ctaUrl) : false

  if (!headline && paragraphs.length === 0 && !imageUrl) return null

  return (
    <CfSectionShell
      id={id}
      paddingTop={paddingTop}
      paddingBottom={paddingBottom}
      showBottomBorder={showBottomBorder}
      theme={theme}
      fullWidth={fullWidth}
      mobileNoTopPadding={mobileNoTopPadding}
    >
      <div
        className={clsx(
          'cf-guides-section-wrap',
          mobileNoTopPadding && 'mobile-pt-0',
        )}
      >
        <section className={clsx('cf-guides-section', !imageUrl && 'no-image')}>
          {imageUrl ? (
            <div className="cf-guides-visual">
              <div className="cf-guides-image-frame">
                <img
                  src={contentfulImage(imageUrl, { w: 800 })}
                  alt={imageAlt}
                />
              </div>
            </div>
          ) : null}

          <div className="cf-guides-content">
            {headline ? <h3 className="heading-l">{headline}</h3> : null}

            {paragraphs.length > 0 ? (
              <div className="cf-guides-copy-group">
                {paragraphs.map((paragraph, index) => (
                  <p
                    key={`guides-copy-${index}`}
                    className="cf-guides-copy text-primary body-l"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            ) : null}

            {ctaLabel && ctaUrl ? (
              <LinkCtaNext
                className="cf-guides-cta"
                external={ctaIsExternal}
                href={ctaIsExternal ? ctaUrl : undefined}
                to={ctaIsExternal ? undefined : ctaUrl}
              >
                {ctaLabel}
              </LinkCtaNext>
            ) : null}
          </div>
        </section>
      </div>
    </CfSectionShell>
  )
}
